import axios, {
  AxiosRequestConfig,
  Method,
  AxiosResponse,
  AxiosError,
  AxiosResponseHeaders,
} from 'axios';
import { stringify } from 'qs';

import { getApiUrl } from 'config/urls';

import { ApiCallArgs, ApiResponse } from './types';

export type BackendOkResponse<DataT = any> = {
  data: DataT;
  config: AxiosRequestConfig;
  meta?: any;
  headers: AxiosResponseHeaders;
};

export type BackendErrorResponse = {
  data: any;
  config: AxiosRequestConfig;
  headers: AxiosResponseHeaders;
  error: {
    status: number;
    name: string;
    message: string;
  };
};

export type BackendResponse<DataT = any> =
  | BackendOkResponse<DataT>
  | BackendErrorResponse;

/**
 * Проверяем, что это положительный HTTP ответ
 * @param resp
 */
const isNetworkOkResponse = <T = any>(
  resp: BackendResponse<T>
): resp is BackendResponse<T> => Boolean(resp);

/**
 * Проверяем, что структура положительного HTTP ответа
 * соответствует структуре ответа нашего бэкенда
 * @param resp
 */
const isBackendOkResponse = <T = any>(
  resp: any
): resp is AxiosResponse<BackendOkResponse<T>> =>
  isNetworkOkResponse(resp) && resp.data;

const isNetworkErrorResponse = <T = any>(resp: any): resp is AxiosResponse<T> =>
  resp.status >= 400;

/**
 * Проверяем, что структура HTTP ответа с ошибкой
 * соответствует структуре ответа с ошибкой нашего бэкенда
 * @param resp
 */
const isBackendErrorResponse = (
  resp: any
): resp is AxiosResponse<BackendErrorResponse> => {
  // Исправление: проверяем правильную структуру ответа бэкенда
  // Бэкенд возвращает {status: "bad_request", message: "...", data: {}}
  // или может быть {error: {status, name, message}} для некоторых случаев
  return (
    isNetworkErrorResponse(resp) &&
    resp.data &&
    (resp.data.error || (resp.data.status && resp.data.message))
  );
};

const baseCallApi = async <DataT = any>(
  url: string,
  method: Method = 'GET',
  config: AxiosRequestConfig = {}
): Promise<BackendResponse<DataT>> =>
  await axios({
    method,
    url,
    params: method.toLowerCase() === 'get' ? config.data : {},
    paramsSerializer: (params) => {
      return stringify(params, {
        indices: false,
      });
    },
    ...config,
  }).then(
    (response: AxiosResponse<DataT>) => response,
    (error: AxiosError) => {
      return error.response as AxiosResponse;
    }
  );

/**
 * Проверяет ответ HTTP на ошибки
 */
async function callBaseCallApiLayer<DataT = any>(
  response?: BackendResponse<DataT>
): Promise<ApiResponse> {
  /**
   * Обрабатываем случай, когда ответа нет вообще
   */
  if (!response) {
    return {
      isError: true,
      data: null,
      rawResponse: null,
      status: 0,
    };
  }

  /**
   * HTTP ответ с ошибкой
   */
  if (isNetworkErrorResponse(response)) {
    /**
     * Ответ соответствует структуре ответа нашего бэкенда
     */
    if (isBackendErrorResponse(response)) {
      // Исправление: извлекаем message из правильной структуры
      // Бэкенд может вернуть либо response.data.error.message, либо response.data.message
      const errorMessage = response.data.error?.message || response.data.message || 'Bad request';
      const errorStatus = response.data.error?.status || response.data.status || 'bad_request';
      
      return {
        isError: true,
        data: null,
        code: String(errorStatus),
        message: String(errorMessage),
        rawResponse: response,
        status: response.status,
      };
    }

    return {
      isError: true,
      data: response.data,
      rawResponse: response,
      status: response.status,
    };
  }

  /**
   * Положительный HTTP ответ
   */
  if (isNetworkOkResponse<DataT>(response) && response.data) {
    /**
     * Этот ответ соответствует структуре нашего бэкенда
     */
    if (isBackendOkResponse(response)) {
      return {
        isError: false,
        data: response.data,
        rawResponse: response,
        status: response.status,
      };
    }

    return {
      isError: false,
      data: null,
      rawResponse: response,
      status: response.data.status,
    };
  }

  /**
   * Произошло что-то совсем неизвестное
   */
  return {
    isError: true,
    data: null,
    rawResponse: response,
    status: 0,
  };
}

export async function callApi<T>({
  endpoint,
  methodType = 'GET',
  config = {},
}: ApiCallArgs): Promise<ApiResponse> {
  const response = await baseCallApi<T>(getApiUrl() + endpoint, methodType, {
    ...config,
  });

  return await callBaseCallApiLayer(response);
}
